package co.maytes.standupbot.dashboard.features.ds

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.FontWeight
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Tracking
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.dashboard.styles.tabularNums
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.dom.Div

private const val SAMPLE = "Standup digest for the platform team"

private val typeStyles: List<Pair<String, Typography.TypeStyle>> = listOf(
    "display" to Typography.display,
    "h1" to Typography.h1,
    "h2" to Typography.h2,
    "h3" to Typography.h3,
    "h4" to Typography.h4,
    "body" to Typography.body,
    "label" to Typography.label,
    "bodySmall" to Typography.bodySmall,
    "caption" to Typography.caption,
    "code" to Typography.code,
    "displayLg" to Typography.displayLg,
    "displayMd" to Typography.displayMd,
    "displaySm" to Typography.displaySm,
    "statValue" to Typography.statValue,
)

private val tabularSamples = listOf("1,111", "8,888", "2,020", "11.1%")

@Composable
fun TypographySection() {
    GallerySection(
        id = "typography",
        title = "Typography",
        description = "Ten text styles plus four mono display styles, then the weight, tracking and tabular-figure tokens.",
    ) {
        GalleryPanel {
            typeStyles.forEach { (name, style) -> TypeRow(name, style) }
        }
        GalleryHeading("Weight")
        GalleryPanel {
            FontWeight.entries.forEach { (name, weight) ->
                SpecimenRow(
                    label = "$name $weight",
                    sampleStyle = "font-weight: $weight; font-size: ${Typography.h4.size}",
                )
            }
        }
        GalleryHeading("Tracking")
        GalleryPanel {
            Tracking.entries.forEach { (name, value) ->
                val text = if (name == "caps") SAMPLE.uppercase() else SAMPLE
                SpecimenRow(
                    label = "$name ${value.value}em",
                    sampleStyle = "letter-spacing: ${value.value}em; font-size: ${Typography.label.size}",
                    text = text,
                )
            }
        }
        GalleryHeading("Tabular figures")
        GalleryNote(
            "Numeric columns and stats use the sb-tabular-nums utility so digits keep a fixed width " +
                "and columns line up as values change.",
        )
        Div(
            attrs = {
                style {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(auto-fit, minmax(200px, 1fr))")
                    property("gap", "16px")
                }
            },
        ) {
            GalleryPanel {
                GalleryMono("proportional")
                tabularSamples.forEach { NumberRow(it, tabular = false) }
            }
            GalleryPanel {
                GalleryMono("tabular-nums")
                tabularSamples.forEach { NumberRow(it, tabular = true) }
            }
        }
    }
}

@Composable
private fun TypeRow(name: String, style: Typography.TypeStyle) {
    Stack(4) {
        GalleryMono("$name · ${style.size} / ${style.lineHeight} / ${style.weight}")
        SpanText(
            text = SAMPLE,
            modifier = Modifier.styleModifier {
                property("font-size", "${style.size}")
                property("line-height", "${style.lineHeight}")
                property("font-weight", "${style.weight}")
                property("font-family", style.fontFamily)
                property("color", Palette.textPrimary.cssRgb())
                property("overflow-wrap", "anywhere")
            },
        )
    }
}

@Composable
private fun SpecimenRow(label: String, sampleStyle: String, text: String = SAMPLE) {
    Stack(2) {
        GalleryMono(label)
        Div(
            attrs = {
                attr("style", "color: ${Palette.textPrimary.cssRgb()}; $sampleStyle")
            },
        ) {
            org.jetbrains.compose.web.dom.Text(text)
        }
    }
}

@Composable
private fun NumberRow(value: String, tabular: Boolean) {
    SpanText(
        text = value,
        modifier = (if (tabular) Modifier.tabularNums() else Modifier).styleModifier {
            property("font-size", "${Typography.h3.size}")
            property("font-weight", "600")
            property("color", Palette.textPrimary.cssRgb())
        },
    )
}
